/**
 * MobileExploreDrawer
 *
 * A persona-aware bottom-sheet that surfaces the 6–8 most-tapped
 * destinations in the app behind a single "Explore" pill in the
 * mobile header. The hamburger drawer still exists for the long-tail
 * (settings, billing, admin tools); this is the "luxury concierge"
 * surface — large cream-paper rows, gold hairlines, magazine-style
 * eyebrows — so first-time users on phones don't have to dig through
 * two levels of menus to find Marketplace, Breeders, Events, etc.
 *
 * Layout decisions:
 *   • Bottom sheet (not a top-of-page sheet) — feels native on iOS
 *     and Android, doesn't fight the OS chrome.
 *   • 2-column grid of large tappable tiles — Apple Store / Hermès
 *     guidelines suggest 56pt min touch target; we use 76pt.
 *   • Each tile is cream paper + gold hairline + Spectral name, so
 *     the drawer feels like the rest of the brand instead of a
 *     generic Material list.
 */
import React from "react";
import { NavLink } from "react-router-dom";
import { isNativeApp } from "@/lib/nativeBridge";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { CompassIcon as Compass, CaretDownIcon as CaretDown, StorefrontIcon as Storefront, UsersFourIcon as UsersFour, PawPrintIcon as PawPrint, CalendarIcon as Calendar, MegaphoneIcon as Megaphone, TreeStructureIcon as TreeStructure, ShieldCheckIcon as ShieldCheck, HeartStraightIcon as HeartStraight, ChartBarIcon as ChartBar, TruckIcon as Truck, DogIcon as Dog, SparkleIcon as Sparkle, BookOpenIcon as BookOpen, WrenchIcon as Wrench, GraduationCapIcon as GraduationCap, TreeIcon as Tree } from "@phosphor-icons/react";

// Most-popular destinations — surfaced at the top of the drawer for
// every role so Calendar, Service Pros & Storefronts are one tap away
// (mirrors the desktop Explore dropdown's "Most popular" group).
const POPULAR_TILES = [
  { to: "/calendar", label: "My Calendar", hint: "Heats, breedings & reminders", icon: Calendar },
  { to: "/find-pros", label: "Find a Service Pro", hint: "Trainers · groomers · walkers · sitters", icon: GraduationCap },
  { to: "/find-pros?kind=field_host", label: "Zoomie Time", hint: "Rent a private field for off-leash play", icon: Tree },
  { to: "/storefronts", label: "Storefronts", hint: "Pet supplies & merch from breeders", icon: Storefront },
];

const TILES_BY_ROLE = {
  // Owner / general user — emphasizes browsing and discovery.
  default: [
    { to: "/marketplace", label: "Marketplace", hint: "Puppies & adult dogs", icon: Storefront },
    { to: "/breeders", label: "Breeders", hint: "Vetted kennels", icon: UsersFour },
    { to: "/breeds", label: "Breeds", hint: "Catalog & traits", icon: Dog },
    { to: "/dog-log", label: "Dog Log", hint: "Daily care tracking", icon: HeartStraight },
    { to: "/events", label: "Events", hint: "Shows & meetups", icon: Calendar },
    { to: "/shop", label: "The Dog Shop", hint: "Merch & feed", icon: PawPrint },
    { to: "/pedigree", label: "Pedigree", hint: "Lookup any dog", icon: TreeStructure },
    { to: "/feed", label: "The Dog's Voice", hint: "Social media for your dog", icon: Megaphone },
  ],
  breeder: [
    { to: "/marketplace", label: "Marketplace", hint: "List or browse", icon: Storefront },
    { to: "/breeders", label: "Breeders", hint: "Compare kennels", icon: UsersFour },
    { to: "/litters", label: "Litters", hint: "Manage your litters", icon: PawPrint },
    { to: "/dog-log", label: "Dog Log", hint: "Daily care tracking", icon: HeartStraight },
    { to: "/breeder/analytics", label: "Analytics", hint: "KPIs & funnel", icon: ChartBar },
    { to: "/events", label: "Events", hint: "Shows & meetups", icon: Calendar },
    { to: "/feed", label: "The Dog's Voice", hint: "Social media for your dogs", icon: Megaphone },
    { to: "/pedigree", label: "Pedigree", hint: "Build & verify", icon: TreeStructure },
  ],
  buyer: [
    { to: "/marketplace", label: "Marketplace", hint: "Litters & adult dogs", icon: Storefront },
    { to: "/breeders", label: "Breeders", hint: "Vetted kennels", icon: UsersFour },
    { to: "/breeds", label: "Breeds", hint: "Find the right fit", icon: Dog },
    { to: "/watchlist", label: "Watchlist", hint: "Your saved", icon: HeartStraight },
    { to: "/dog-log", label: "Dog Log", hint: "Care tracking", icon: HeartStraight },
    { to: "/events", label: "Events", hint: "Meet breeders", icon: Calendar },
    { to: "/feed", label: "The Dog's Voice", hint: "Dog social media", icon: Megaphone },
    { to: "/shop", label: "The Dog Shop", hint: "Merch & feed", icon: PawPrint },
  ],
  transporter: [
    { to: "/transport", label: "Transport board", hint: "Active jobs", icon: Truck },
    { to: "/transporters", label: "Directory", hint: "Other haulers", icon: UsersFour },
    { to: "/marketplace", label: "Marketplace", hint: "Puppies & adult dogs", icon: Storefront },
    { to: "/feed", label: "The Dog's Voice", hint: "Dog social media", icon: Megaphone },
    { to: "/events", label: "Events", hint: "Shows & meetups", icon: Calendar },
    { to: "/shop", label: "The Dog Shop", hint: "Merch & feed", icon: PawPrint },
  ],
  admin: [
    { to: "/admin", label: "Operations", hint: "Today's health", icon: Sparkle },
    { to: "/admin/users", label: "Users", hint: "Search & impersonate", icon: UsersFour },
    { to: "/admin/approvals", label: "Approvals", hint: "Transfers & verifications", icon: ShieldCheck },
    { to: "/admin/payments", label: "Payments", hint: "Reconcile & refund", icon: ChartBar },
    { to: "/marketplace", label: "Marketplace", hint: "Browse as user", icon: Storefront },
    { to: "/breeders", label: "Breeders", hint: "Verify kennels", icon: UsersFour },
    { to: "/dog-log", label: "Dog Log", hint: "Care tracking", icon: HeartStraight },
    { to: "/feed", label: "The Dog's Voice", hint: "Social feed · moderate", icon: Megaphone },
  ],
};

function pickTiles(user, navCtx) {
  const role = (user?.role || "").toLowerCase();
  const persona = (user?.persona || "").toLowerCase();
  const isProvider =
    persona === "service_provider" ||
    (navCtx?.available || []).includes("service_provider");
  let base;
  if (role === "admin") base = TILES_BY_ROLE.admin;
  else if (persona === "transporter") base = TILES_BY_ROLE.transporter;
  else if (persona === "breeder") base = TILES_BY_ROLE.breeder;
  else if (persona === "buyer") base = TILES_BY_ROLE.buyer;
  else base = TILES_BY_ROLE.default;
  // "My Storefront" is always pinned last so every signed-in user
  // can reach their seller hub in one tap from the mobile Explore
  // drawer (matches the desktop Explore dropdown). /me/store handles
  // the onboarding cascade for users who don't yet have a store.
  const extras = [
    {
      to: "/journal",
      label: "Journal",
      hint: "Notes from the registry",
      icon: BookOpen,
    },
    {
      to: "/me/store",
      label: "My Storefront",
      hint: "Products · orders · merch",
      icon: Storefront,
      featured: true,
    },
  ];
  // Service pros get a one-tap featured tile straight to their
  // provider page — bookings, services, profile edits.
  if (isProvider) {
    extras.unshift({
      to: "/provider/me",
      label: "My Provider page",
      hint: "Bookings · services · edit profile",
      icon: Wrench,
      featured: true,
    });
  }
  const seen = new Set(POPULAR_TILES.map((t) => t.to));
  return [...POPULAR_TILES, ...base.filter((t) => !seen.has(t.to)), ...extras];
}

export default function MobileExploreDrawer({ user, navCtx }) {
  const [open, setOpen] = React.useState(false);
  const tiles = pickTiles(user, navCtx);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          data-testid="mobile-explore-trigger"
          aria-label="Open Explore"
          className="md:hidden inline-flex items-center gap-1 h-10 pl-3 pr-2.5 rounded-full border border-[#C9A227]/40 bg-[#FBF7EC] text-ink text-[12px] font-bold tracking-[0.04em] hover:bg-[#F3E9C7] hover:border-[#C9A227] active:scale-95 transition-all shadow-[0_1px_2px_rgba(193,162,39,0.15)]"
        >
          <Compass size={13} weight="duotone" className="text-[#8A6A00]" />
          <span>Explore</span>
          <CaretDown size={10} weight="bold" className="text-[#8A6A00]" />
        </button>
      </SheetTrigger>
      <SheetContent
        side="bottom"
        className="rounded-t-3xl border-t border-[#C9A227]/30 p-0 max-h-[85dvh] bg-[#FBF7EC] flex flex-col overflow-hidden"
        data-testid="mobile-explore-drawer"
      >
        <VisuallyHidden>
          <SheetTitle>Explore</SheetTitle>
          <SheetDescription>Jump to the most-used registry sections</SheetDescription>
        </VisuallyHidden>
        {/* iOS-style grabber */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="h-1.5 w-12 rounded-full bg-[#C9A227]/30" aria-hidden="true" />
        </div>
        <div className="px-6 pt-2 pb-1">
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#8A6A00]">
            Registry · Explore
          </p>
          <h2 className="font-spectral text-[22px] tracking-tight text-ink leading-tight mt-0.5">
            Where do you want to go?
          </h2>
          <div className="h-px bg-gradient-to-r from-[#C9A227]/60 via-[#C9A227]/20 to-transparent mt-3" />
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch] px-5 pt-4 pb-[calc(1.5rem+env(safe-area-inset-bottom))]">
          <div className="grid grid-cols-2 gap-3">
            {tiles.map((t) => (
              <NavLink
                key={t.to}
                to={t.to}
                onClick={() => setOpen(false)}
                data-testid={`explore-tile-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`group flex items-start gap-3 rounded-2xl active:scale-[0.98] transition-all p-3.5 ${
                  t.featured
                    ? "bg-ink text-white border-2 border-[#C9A227] col-span-2 shadow-[0_2px_12px_rgba(193,162,39,0.25)] hover:bg-[#1a1a1a]"
                    : "bg-white/80 border border-[#E8D9A6]/70 hover:border-[#C9A227] shadow-[0_1px_2px_rgba(0,0,0,0.02)]"
                }`}
              >
                <span className={`inline-flex items-center justify-center h-10 w-10 rounded-xl flex-shrink-0 ${
                  t.featured
                    ? "bg-gold-gradient text-ink ring-1 ring-[#C9A227]/50"
                    : "bg-gradient-to-br from-[#FBF7EC] to-[#F3E9C7] border border-[#C9A227]/30 text-[#8A6A00]"
                }`}>
                  <t.icon size={18} weight="duotone" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className={`font-spectral text-[15px] leading-tight truncate ${t.featured ? "text-white" : "text-ink"}`}>
                    {t.label}
                  </p>
                  <p className={`text-[11px] mt-0.5 truncate ${t.featured ? "text-white/70" : "text-[#6B6B6B]"}`}>
                    {t.hint}
                  </p>
                </div>
              </NavLink>
            ))}
          </div>
          <p className="text-[10.5px] text-[#6B6B6B]/80 text-center mt-5 italic">
            {isNativeApp()
              ? "Looking for settings or your kennel? Use the menu (top-left)."
              : "Looking for settings, billing, or your kennel? Use the menu (top-left)."}
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
